/**
 * HoverPreview (DESIGN.md T13): a preview card next to an item while the mouse rests on it (Outlook/Google event
 * peek). Items inside carry `data-context-id`; `render(id)` gives the card (undefined = none). Mouse only: touch
 * and keyboard users open the item itself (its click/Enter shows the same details in the panel). The card stays
 * open while the pointer is on it; Esc or leaving closes it.
 */
import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { Popover, PopoverAnchor, PopoverContent } from '../components/popover';
import { CONTEXT_ID_ATTRIBUTE } from '../actions/context-menu-area';

export interface HoverPreviewProps {
  render: (id: string) => ReactNode | undefined;
  /** Rest time before the card opens (ms). */
  delay?: number;
  /** Suppress (e.g. while dragging or a menu is open). */
  disabled?: boolean;
  children: ReactNode;
}

const CLOSE_DELAY_MS = 200;

export function HoverPreview({ render, delay = 600, disabled = false, children }: HoverPreviewProps) {
  const [open, setOpen] = useState<{ id: string; rect: DOMRect }>();
  const openTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hovered = useRef<string | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(openTimer.current);
      clearTimeout(closeTimer.current);
    },
    [],
  );

  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(undefined), CLOSE_DELAY_MS);
  };

  const onPointerOver = (event: PointerEvent) => {
    if (event.pointerType !== 'mouse' || disabled || event.buttons !== 0) return;
    const item = event.target instanceof Element ? event.target.closest<HTMLElement>(`[${CONTEXT_ID_ATTRIBUTE}]`) : null;
    const candidate = item?.getAttribute(CONTEXT_ID_ATTRIBUTE) ?? undefined;
    // Items without a card (free time) count as "not on an item".
    const id = candidate !== undefined && render(candidate) !== undefined ? candidate : undefined;
    if (id === hovered.current) return;
    hovered.current = id;
    clearTimeout(openTimer.current);
    if (!item || !id) {
      if (open) scheduleClose();
      return;
    }
    clearTimeout(closeTimer.current);
    openTimer.current = setTimeout(() => {
      if (render(id) !== undefined) setOpen({ id, rect: item.getBoundingClientRect() });
    }, open ? 0 : delay);
  };
  const onPointerLeave = () => {
    hovered.current = undefined;
    clearTimeout(openTimer.current);
    scheduleClose();
  };
  const cancel = () => {
    clearTimeout(openTimer.current);
    hovered.current = undefined;
    setOpen(undefined);
  };

  const content = open && !disabled ? render(open.id) : undefined;
  return (
    <>
      <div className="contents" onPointerOver={onPointerOver} onPointerLeave={onPointerLeave} onPointerDown={cancel} onContextMenu={cancel}>
        {children}
      </div>
      {open && content !== undefined && (
        <Popover open onOpenChange={(value) => !value && setOpen(undefined)}>
          <PopoverAnchor asChild>
            <span aria-hidden className="pointer-events-none fixed" style={{ left: open.rect.left, top: open.rect.top, width: open.rect.width, height: open.rect.height }} />
          </PopoverAnchor>
          <PopoverContent
            side="right"
            align="start"
            className="w-80"
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            onPointerEnter={() => clearTimeout(closeTimer.current)}
            onPointerLeave={scheduleClose}
          >
            {content}
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}
